/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/
  
import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { classes } from '@adobe-commerce/elsie/lib';
import '@/quote-management/components/QuotePricesSummary/QuotePricesSummary.css';
import { Accordion, AccordionSection } from '@adobe-commerce/elsie/components';

interface Entry {
  label: string;
  id: string;
  value: VNode;
  strong?: boolean;
  children?: Entry[];
}

export interface QuotePricesSummaryProps extends HTMLAttributes<HTMLDivElement> {
  entries?: Entry[];
}

export const QuotePricesSummary: FunctionComponent<QuotePricesSummaryProps> = ({
  className,
  entries,
  ...props
}) => {
  const createEntry = (entry: Entry) => {
    const children = entry.children?.map(createEntry);
    return (
      <div key={entry.id} className="quote-management-quote-prices-summary__entry" data-testid={`quote-prices-summary-entry-${entry.id}`}>
        {children ? <Accordion
          className="quote-management-quote-prices-summary__accordion"
          data-testid={`quote-prices-summary-entry-accordion-${entry.id}`}
        >
          <AccordionSection className="quote-management-quote-prices-summary__accordion-section" title={entry.label} ariaLabelTitle={entry.label} secondaryText={entry.value}>
            {children}
          </AccordionSection>
        </Accordion>
        :
        <>
          <span className={classes(['quote-management-quote-prices-summary__label', ['quote-management-quote-prices-summary__label--strong', entry.strong]])} data-testid={`quote-prices-summary-entry-label-${entry.id}`}>{entry.label}</span>
          <span className="quote-management-quote-prices-summary__value" data-testid={`quote-prices-summary-entry-value-${entry.id}`}>{entry.value}</span>
        </>
        }
      </div>
    );
  };

  return (
    <div className={classes(['quote-management-quote-prices-summary', className])} data-testid="quote-prices-summary" {...props}>
      {entries?.map(createEntry)}
    </div>
  );
};
